import { Injectable, input } from '@angular/core';
import { GenericError } from '@models/classes/generic.error';
import { ErrorCode } from '@models/enums/error.codes';
import { PerformAuditAttachmentLocation } from '@models/enums/perform-attachment-location.enum';

@Injectable({
    providedIn: 'root',
})
export class FileSystemService {
    constructor() { }

    /**
     * Get root directory of app
     * @returns returns root directory
     */
    getRootDirectory(): Promise<FileSystemDirectoryHandle> {
        if (!('storage' in navigator)) {
            const err = new GenericError();
            err.message = 'storage api not supported';
            throw err;
        }
        return navigator.storage.getDirectory();
    }

    async getPerformDir() {
        const rootDir = await this.getRootDirectory();
        const performDir = await rootDir.getDirectoryHandle('perform', {
            create: true,
        });
        return performDir;
    }

    /**
     * Get database directory
     * @returns returns database directory
     */
    async getDatabaseDirectory() {
        const performDir = await this.getPerformDir();
        const dbDir = await performDir.getDirectoryHandle('database', {
            create: true,
        });
        return dbDir;
    }

    /**
     * Show file picker dialog
     * @returns returns picked file or null if file not selected
     */
    showFilePickerDialog(): Promise<File | null>;
    showFilePickerDialog(multiple: true): Promise<File[] | null>;
    showFilePickerDialog(multiple?: true): Promise<File | File[] | null> {
        const inputFile = document.createElement('input');
        inputFile.type = 'file';
        if (multiple === true) {
            inputFile.multiple = true;
        }
        inputFile.click();
        return new Promise<File | null>((resolve, reject) => {
            inputFile.onchange = () => {
                if (inputFile.files && inputFile.files.length > 0) {
                    if (multiple === true) {
                        const files: File[] = [];
                        for (let index = 0; index < inputFile.files.length; index++) {
                            const element = inputFile.files.item(index);
                            if (element) {
                                files.push(element);
                            }
                        }
                        resolve(files as any);
                    } else {
                        resolve(inputFile.files.item(0));
                    }
                } else {
                    resolve(null);
                }
            };
        });
    }

    /**
     * Save file in database directory
     * @param file file to save in database directory
     * @param fileName specify a file name to be save
     */
    async saveFileInDBDirectory(file: File, fileName: string): Promise<void> {
        const dbDir = await this.getDatabaseDirectory();
        const fileHandle = await dbDir.getFileHandle(fileName, {
            create: true,
        });
        const writable = await fileHandle.createWritable();
        await writable.write(file);
        await writable.close();
    }

    /**
     * Get all file names in db directory
     * @returns returns all file names in db directory
     */
    async getAllFileNamesInDBDirectory(): Promise<string[]> {
        const dbDir = await this.getDatabaseDirectory();
        const files = dbDir.entries();
        const fileNames: string[] = [];
        for await (const file of files) {
            if (file[1].kind === 'file') {
                fileNames.push(file[1].name);
            }
        }
        return fileNames;
    }

    /**
     * Export database to user file system
     * @param fileName database name
     */
    async exportDB(fileName: string) {
        const dbDir = await this.getDatabaseDirectory();
        const fileHandle = await dbDir.getFileHandle(fileName, {
            create: false,
        });
        const file = await fileHandle.getFile();
        const shareData: ShareData = {
            files: [file],
            title: 'Database File',
            text: 'Share the database file',
        };
        if (navigator.canShare()) {
            await navigator.share(shareData);
        } else {
            const aTag = document.createElement('a');
            aTag.href = URL.createObjectURL(file);
            aTag.download = fileName;
            aTag.click();
            URL.revokeObjectURL(aTag.href);
        }
    }
    
    async getAvailableFileNameInDirectory(fileName: string, [visitNo, targetLocation, auditNo]: Parameters<typeof this.getVisitAttDir>): Promise<string> {

        const fileExists = async (fileName: string, targetDirectory: FileSystemDirectoryHandle): Promise<boolean> => {
            const fileHandle = await targetDirectory.getFileHandle(fileName, { create: false }).catch(err => {
                console.log(err)
                return null
            });
            if (fileHandle) {
                return true;
            }
            return false;
        }

        const targetDirectory = await this.getVisitAttDir(visitNo, targetLocation, auditNo);
        if (!(await fileExists(fileName, targetDirectory))) {
            return fileName;
        }

        // Simple parsing of the file name into base and extension.
        const dotIndex = fileName.lastIndexOf(".");
        const base = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
        const ext = dotIndex > 0 ? fileName.substring(dotIndex) : "";

        // Increment the counter until an available file name is found.
        let counter = 1;
        let newFileName = "";
        while (true) {
            newFileName = `${base}_${counter}${ext}`;
            if (!(await fileExists( newFileName, targetDirectory))) {
                return newFileName;
            }
            counter++;
        }


    }

    async getVisitAttDir(visitNo: string, targetLocation: PerformAuditAttachmentLocation): Promise<FileSystemDirectoryHandle>;
    async getVisitAttDir(visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo: string,): Promise<FileSystemDirectoryHandle>;
    async getVisitAttDir(visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo?: string): Promise<FileSystemDirectoryHandle> {
        let targetDirectory: FileSystemDirectoryHandle;
        const performRootDir = await this.getPerformDir();
        const visitAttDir = await performRootDir.getDirectoryHandle(`visit-attachment-${visitNo}`, {
            create: true,
        });
        if (auditNo) {
            const auditAttDir = await visitAttDir.getDirectoryHandle(`audit-${auditNo}`, {
                create: true,
            });
            if (targetLocation == PerformAuditAttachmentLocation.Audit) {
                targetDirectory = auditAttDir;
            } else if (targetLocation == PerformAuditAttachmentLocation.Attempted) {
                targetDirectory = await auditAttDir.getDirectoryHandle(`audit-attempt-att`, {
                    create: true,
                });
            } else if (targetLocation == PerformAuditAttachmentLocation.Questions) {
                targetDirectory = await auditAttDir.getDirectoryHandle(`ques-att`, {
                    create: true,
                });
            } else {
                throw new GenericError({
                    errorCode: ErrorCode.INVALID_DIRECTORY,
                    message: `Invalid directory location. Value is ${targetLocation}`
                })
            }
        } else {
            if (targetLocation == PerformAuditAttachmentLocation.SubmitFormSignature) {
                targetDirectory = await visitAttDir.getDirectoryHandle(`submit-signature`, {
                    create: true,
                });
            } else {
                throw new GenericError({
                    errorCode: ErrorCode.INVALID_DIRECTORY,
                    message: `Invalid directory location. Value is ${targetLocation}`
                })
            }
        }

        return targetDirectory;
    }


    async saveAttachments(attachments: Map<string, File>, visitNo: string, targetLocation: PerformAuditAttachmentLocation): Promise<void>;
    async saveAttachments(attachments: Map<string, File>, visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo: string,): Promise<void>;
    async saveAttachments(attachments: Map<string, File>, visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo?: string): Promise<void> {
        let targetDirectory: FileSystemDirectoryHandle;
        if (typeof auditNo === 'string') {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation, auditNo);
        }
        else {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation);
        }

        for await (const [name, file] of attachments) {
            const fileHandle = await targetDirectory.getFileHandle(name, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(file);
            await writable.close();
        }

    }

    async clearVisitAttachments(visitNo: string): Promise<void> {
        const visitAttDir = await this.getVisitAttDir(visitNo, PerformAuditAttachmentLocation.SubmitFormSignature);
        const entries = visitAttDir.entries();
        for await (const file of entries) {
                await visitAttDir.removeEntry(file[1].name, {recursive : true}).catch(err => {
                    console.log('failed to delete item', file[1]);
                });
        }
    }

    async deleteAttachment(attachmentNames: string[], visitNo: string, targetLocation: PerformAuditAttachmentLocation): Promise<void>;
    async deleteAttachment(attachmentNames: string[], visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo: string): Promise<void>;
    async deleteAttachment(attachmentNames: string[], visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo?: string): Promise<void> {
        let targetDirectory: FileSystemDirectoryHandle;
        if (typeof auditNo === 'string') {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation, auditNo);
        }
        else {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation);
        }

        for await (let att of attachmentNames) {
            const file = await targetDirectory.getFileHandle(att, { create: false }).catch((err) => null);
            if (file) {
                await targetDirectory.removeEntry(att);
            }
        }
    }


    async getVisitAttachments(visitNo: string, fileNames: string[], targetLocation: PerformAuditAttachmentLocation): Promise<Map<string, File>>;
    async getVisitAttachments(visitNo: string, fileNames: string[], targetLocation: PerformAuditAttachmentLocation, auditNo: string): Promise<Map<string, File>>;
    async getVisitAttachments(visitNo: string, fileNames: string[], targetLocation: PerformAuditAttachmentLocation, auditNo?: string): Promise<Map<string, File>> {
        let targetDirectory: FileSystemDirectoryHandle;
        if (typeof auditNo === 'string') {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation, auditNo);
        }
        else {
            targetDirectory = await this.getVisitAttDir(visitNo, targetLocation);
        }
        const files: Map<string, File> = new Map();
        if (fileNames.length > 0) {
            for await (let fileName of fileNames) {
                const fileHandle = (await targetDirectory
                    .getFileHandle(fileName, { create: false })
                    .catch((err) => {
                        console.log(err)
                        return null;
                    })) as FileSystemFileHandle | null;
                if (fileHandle) {
                    const file = await fileHandle.getFile();
                    files.set(fileName, file);
                }
            }
        }
        return files;
    }
}
